import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Save, Plus, Trash2, Rocket } from 'lucide-react';
import authedFetch from '../../lib/authedFetch';
import { SelectField, TextField } from './FieldKit';

const API = process.env.REACT_APP_BACKEND_URL;

const STAKEHOLDER_ROLES = ['CFO', 'CHRO', 'L&D Lead', 'CEO', 'COO', 'CTO', 'Other'];

export default function StepReviewActivate({ orgId, step, state, onSave, saving, shareToken }) {
  const [draft, setDraft] = useState(state.answers || {});
  const [resolved, setResolved] = useState(null);
  useEffect(() => { setDraft(state.answers || {}); }, [state]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (shareToken) return;
    (async () => {
      try {
        const r = await authedFetch(`${API}/api/organizations/${orgId}/roi-profile`);
        if (r.ok) {
          const d = await r.json();
          setResolved(d.resolved);
        }
      } catch { /* noop */ }
    })();
  }, [orgId, shareToken]);

  const addStakeholder = () => {
    setDraft((d) => ({
      ...d,
      stakeholders: [...(d.stakeholders || []), { role: 'CFO', name: '', email: '' }],
    }));
  };
  const updateStakeholder = (i, patch) => {
    setDraft((d) => ({
      ...d,
      stakeholders: (d.stakeholders || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  };
  const removeStakeholder = (i) => {
    setDraft((d) => ({
      ...d,
      stakeholders: (d.stakeholders || []).filter((_, idx) => idx !== i),
    }));
  };

  // First-call ROI preview — very lightweight: use the resolved profile's
  // first KPI baseline + a midline 1.5% uplift estimate. This is a
  // narrative hook, not a real run.
  const firstKpi = resolved?.kpis?.[0];
  const previewUplift = firstKpi
    ? Math.round((resolved?.max_kpi_uplift_percent ?? 3.0) * 0.6 * firstKpi.baseline_per_employee * 100) / 100
    : null;

  return (
    <div className="space-y-5" data-testid="onboarding-step-review">
      {/* Stakeholders */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Key stakeholders</h3>
            <p className="text-xs text-slate-500">CFO, CHRO, L&D Lead — used for narrative personalization in prospectus exports.</p>
          </div>
          <Button variant="outline" size="sm" onClick={addStakeholder} data-testid="onb-stakeholder-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(draft.stakeholders || []).map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_auto] gap-2" data-testid={`onb-stakeholder-row-${i}`}>
              <select
                value={s.role}
                onChange={(e) => updateStakeholder(i, { role: e.target.value })}
                className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                data-testid={`onb-stakeholder-role-${i}`}
              >
                {STAKEHOLDER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <Input
                value={s.name || ''}
                onChange={(e) => updateStakeholder(i, { name: e.target.value })}
                placeholder="Name"
                data-testid={`onb-stakeholder-name-${i}`}
              />
              <Input
                type="email"
                value={s.email || ''}
                onChange={(e) => updateStakeholder(i, { email: e.target.value })}
                placeholder="email@example.com"
                data-testid={`onb-stakeholder-email-${i}`}
              />
              <Button variant="ghost" size="sm" onClick={() => removeStakeholder(i)} data-testid={`onb-stakeholder-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
              </Button>
            </div>
          ))}
          {(draft.stakeholders || []).length === 0 && (
            <p className="text-xs text-slate-400 py-2">No stakeholders added yet.</p>
          )}
        </div>
      </div>

      {/* Methodology + go-live */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {step.fields.filter((f) => ['roi_methodology', 'go_live_date'].includes(f.key)).map((f) => (
          f.type === 'enum'
            ? <SelectField key={f.key} field={f} value={draft[f.key]} onChange={set} testIdPrefix="onb-review" />
            : <TextField key={f.key} field={f} value={draft[f.key]} onChange={set} testIdPrefix="onb-review" />
        ))}
      </div>

      {/* First-call ROI preview */}
      {!shareToken && resolved && firstKpi && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5 space-y-2" data-testid="onb-roi-preview">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-emerald-900">First-call ROI preview</h3>
          </div>
          <p className="text-xs text-emerald-800">
            Based on the industry profile + your overrides, a typical mid-effort intervention against
            <strong> {firstKpi.name}</strong> would forecast around
            <strong> ${previewUplift?.toLocaleString()}</strong>/employee/yr (using the industry cap of{' '}
            {(resolved?.max_kpi_uplift_percent ?? 3).toFixed(1)}% at 60% of max).
            This is a narrative hook — your real numbers come from Impact Modelling once data flows in.
          </p>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(draft)} disabled={saving} data-testid="onb-review-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-2" />}
          Save
        </Button>
      </div>
    </div>
  );
}
